import { RestrictedTag } from '@apis/TagManager/model';
import { ActionIcon, Checkbox, Collapse, Group, Space, Text, Tooltip, useMantineTheme } from '@mantine/core';
import { SettingsDivider } from '@root/Components/DashboardLayout/Charts/Design';
import { IconPicker } from '@root/Components/Picker/IconPicker';
import { TagKeyCombobox } from '@root/Components/Resources/Common/ResourceTags';
import { getInputBinding } from '@root/Components/Settings/InputSpec';
import { SettingsInput, SettingsLabeled, useInputBinding } from '@root/Components/Settings/SettingsInputs';
import { TooltipWhite } from '@root/Design/Primitives';
import {
    SettingsInfoRow,
    SettingsInfoRowText,
    SettingsInputBox,
    SettingsInputRow,
    SettingsInteractiveRow,
    SettingsLabel,
    SettingsSectionBodyDivider,
    SettingsSectionItem,
    SettingsSectionItemBody,
} from '@root/Design/Settings';
import { SectionExpander } from '@root/Design/Switches';
import { useFmtSvc } from '@root/Services/FormatService';
import { useEffect, useMemo, useState } from 'react';
import { Tag, Trash } from 'tabler-icons-react';

export const RestrictedTagEditor = (props: {
    restrictedTag: RestrictedTag;
    canRemove: boolean;
    handleRemoveRow: (index: number) => void;
    index?: number;
    disabled?: boolean;
    onChange: (index: number, updated: RestrictedTag) => void;
}) => {
    const theme = useMantineTheme();
    const fmtSvc = useFmtSvc();
    const [restrictedTag, setRestrictedTag] = useState<RestrictedTag>();
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    useEffect(() => {
        if (props.restrictedTag) {
            setRestrictedTag(props.restrictedTag);
        }
    }, []);

    useEffect(() => {
        if (restrictedTag?.TagName !== '') {
            handleTagChange();
        }
    }, [restrictedTag]);

    const handleTagChange = () => {
        props.onChange(props.index ?? 0, restrictedTag ?? props.restrictedTag);
    };

    const handleInputChange = (value: string) => {
        setRestrictedTag({
            ...restrictedTag,
            TagName: value,
        });
    };

    const canRemoveButton = props.canRemove ? (
        <Tooltip label="Remove">
            <ActionIcon
                onClick={() => props.handleRemoveRow && props.handleRemoveRow(props?.index ?? 0)}
                color="error"
                sx={{ marginRight: 0, fontSize: theme.fontSizes.xl }}
            >
                <Trash size={18} />
            </ActionIcon>
        </Tooltip>
    ) : null;

    type ScoreDescriptor = { option: keyof RestrictedTag; label: string; description: string };
    const scoreDescriptors = useMemo(
        () =>
            [
                {
                    option: 'CoverageScore',
                    label: 'Coverage Score',
                    description: 'If selected, any resource missing this tag will have a Coverage Score of 0%. ',
                },
                {
                    option: 'ComplianceScore',
                    label: 'Compliance Score',
                    description:
                        'If selected, any resource which does not meet its Tag Policy requirements for this tag will have a reduced Compliance Score. ',
                },
                {
                    option: 'ClarityScore',
                    label: 'Clarity Score',
                    description:
                        'If selected, any resource which appears to have syntax errors for this tag or its values will have a reduced Clarity Score. ',
                },
                {
                    option: 'CompletenessScore',
                    label: 'Completeness Score',
                    description:
                        'If selected, any child or parent resource which does not match its counterpart for this tag will have a reduced Completeness Score. ',
                },
            ] as ScoreDescriptor[],
        []
    );
    type ScoreOption = ScoreDescriptor & { toggle: () => void; isChecked: boolean };
    const scoreOptions = useMemo(
        () =>
            scoreDescriptors.map((d) => ({
                ...d,
                isChecked: restrictedTag?.[d.option] ?? false,
                toggle: () => setRestrictedTag({ ...restrictedTag, [d.option]: !restrictedTag?.[d.option] }),
            })) as ScoreOption[],
        [scoreDescriptors, restrictedTag, setRestrictedTag]
    );
    const anyChecked = scoreOptions.some((o) => o.isChecked) || undefined;
    const allChecked = !!scoreOptions.every((o) => o.isChecked);
    const allIndeterminate = allChecked ? undefined : anyChecked;
    const handleCheckAll = (checked: boolean) => {
        setRestrictedTag({
            ...restrictedTag,
            ...scoreOptions.reduce(
                (next, o) => ({
                    ...next,
                    [o.option]: checked,
                }),
                {} as Partial<RestrictedTag>
            ),
        });
    };

    const description = useInputBinding(restrictedTag ?? {}, (t) => t.Description as string | undefined, '');
    const icon = useInputBinding(restrictedTag ?? {}, (t) => t.Icon as RestrictedTag['Icon']);
    const scoreTooltip = !anyChecked
        ? 'No resource tag scores will not be affected by this tag'
        : allChecked
        ? 'All resource tag scores will be affected by this tag'
        : `This tag will affect resource tag scores for ${fmtSvc.userFriendlyJoin(
              scoreOptions.filter((o) => o.isChecked).map((o) => o.label.replace(' Score', ''))
          )}`;

    return (
        <SettingsSectionItem>
            <SettingsSectionItemBody>
                <Group position="apart" spacing={4}>
                    <TooltipWhite label="The icon is shown in-app in most places where this tag appears." position="left">
                        <SettingsInputBox width="auto" style={{ alignSelf: 'stretch', height: 'auto' }}>
                            <IconPicker
                                noneIcon={
                                    <ActionIcon>
                                        <Tag />
                                    </ActionIcon>
                                }
                                onChange={icon.onChange}
                                value={icon.value}
                            />
                        </SettingsInputBox>
                    </TooltipWhite>
                    <TagKeyCombobox
                        inputProps={{
                            size: 'sm',
                            label: '',
                            placeholder: 'Type or select a Tag Key...',
                            wrapperProps: { sx: { flex: 1 } },
                        }}
                        onChange={handleInputChange}
                        value={restrictedTag?.TagName ?? ''}
                    />
                    {canRemoveButton}
                </Group>
                <SettingsSectionBodyDivider />
                <SettingsInput
                    type="string-multiline"
                    onChange={description.onChange}
                    value={description.value ?? ''}
                    defaultValue=""
                    label="Help Text"
                    presentationOptions={{
                        fullWidth: true,
                        placeholder: `Example: ${
                            restrictedTag?.TagName ?? 'This tag'
                        } is used for allocating departmental costs and should exactly match asset ID's from the company-wide CMDB...`,
                    }}
                />
                <Text color="dimmed" size="xs">
                    Help text is shown in-app as a help bubble when the tag is hovered.
                </Text>
                <SettingsSectionBodyDivider />

                <Group noWrap spacing={0}>
                    <TooltipWhite position="left" label={scoreTooltip}>
                        <Checkbox
                            mr="xs"
                            checked={anyChecked}
                            indeterminate={allIndeterminate}
                            onChange={() => handleCheckAll(!anyChecked)}
                            sx={{ input: { cursor: 'pointer', '&:hover': { outline: `solid 3px ${theme.colors.primary[3]}` } } }}
                        />
                    </TooltipWhite>
                    <Tooltip withinPortal label={`${isExpanded ? 'Hide' : 'Show'} Score Options`}>
                        <SettingsInteractiveRow onClick={() => setIsExpanded((v) => !v)} style={{ flex: 1 }}>
                            <SettingsLabel>Tag Health Scores</SettingsLabel>
                            <SectionExpander size={20} strokeWidth={2} expanded={!isExpanded} />
                        </SettingsInteractiveRow>
                    </Tooltip>
                </Group>
                <Collapse in={isExpanded}>
                    {scoreOptions.map((item) => (
                        <TooltipWhite withinPortal multiline width={300} position="left" label={item.description}>
                            <SettingsInteractiveRow key={item.option} onClick={item.toggle} style={{ flex: 1 }}>
                                <Group spacing={0} noWrap>
                                    <Checkbox
                                        color="primary.5"
                                        mr="xs"
                                        checked={item.isChecked}
                                        onChange={() => {}}
                                        sx={{ transform: 'scale(.9)' }}
                                    />
                                    <SettingsLabel>{item.label}</SettingsLabel>
                                </Group>
                            </SettingsInteractiveRow>
                        </TooltipWhite>
                    ))}
                </Collapse>
                <SettingsInfoRow style={{ paddingTop: 12, paddingBottom: 12 }}>
                    <SettingsInfoRowText>
                        When Tag Health Scores are enabled, resource tag scores are based only on the tags where scores are enabled. If no tag's have
                        scores enabled, scores are based on all tags.
                    </SettingsInfoRowText>
                </SettingsInfoRow>
            </SettingsSectionItemBody>
        </SettingsSectionItem>
    );
};
